import { Send, Search, X } from "lucide-react";
import React, { useState, useMemo, useEffect } from "react";
import { generateColumnsFromData } from "../../utils/constants";
import useKeyboardNavigation from "../../hooks/useKeyboardNavigation";

const TransferOrderModal = ({
  selectedDocType = "Quote",
  handleTransfer = () => {},
  selectedOrders = [],
  orders = [],
  handleSelectOrder = () => {},
  handleSelectAll = () => {},
  searchTerm = "",
  setSearchTerm = () => {},
  fetchModalData = () => {},
  loadingModalData,
  setSelectedOrders,
  getOrderId,
}) => {
  // console.log("Selected Orders", selectedOrders);
  // Keyboard navigation hook
  const { selectedIndex, rowRefs } = useKeyboardNavigation({
    list: orders,
    onEnter: (order) => {
      handleSelectOrder(getOrderId(order));
    },
    isActive: !loadingModalData && orders.length > 0,
    enableScroll: true,
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchModalData();
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const handleTransferClick = () => {
    const selectedRows = orders.filter((order) =>
      selectedOrders.includes(getOrderId(order))
    );
    handleTransfer(selectedDocType, selectedRows);
    // console.log("Selected rows", selectedRows);
    // console.log("selectedDocType", selectedDocType);
    setSelectedOrders([]);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  const removeSelection = (quotNo) => {
    handleSelectOrder(quotNo);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";

    if (dateString.includes("T")) {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }

    return dateString;
  };

  const formatCellValue = (value, field) => {
    const dateFields = [
      "DATE",
      "CREATED",
      "UPDATED",
      "MODIFIED",
      "DELDATE",
      "QUOTDATE",
    ];
    const isDateField = dateFields.some((df) =>
      field.toUpperCase().includes(df)
    );

    if (isDateField && typeof value === "string") {
      return formatDate(value);
    }

    return value;
  };

  const columns = useMemo(() => generateColumnsFromData(orders), [orders]);

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200 shadow-md">
        <div className="px-6 py-5">
          <div className="flex lg:flex-row flex-col items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-2 rounded-lg shadow-sm">
                <Send className="text-white" size={24} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-800">
                  {`${selectedDocType} Transfer`}
                </h1>
                {/* <p className="text-sm text-slate-500 mt-0.5">
                  Select documents to transfer (Use ↑↓ arrows & Enter)
                </p> */}
              </div>
            </div>
            <button
              onClick={handleTransferClick}
              disabled={selectedOrders.length === 0}
              className={`flex items-center gap-2.5 px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                selectedOrders.length > 0
                  ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  : "bg-slate-200 text-slate-400 cursor-not-allowed"
              }`}
            >
              <Send size={18} />
              Transfer Documents
              {selectedOrders.length > 0 && (
                <span className="bg-white text-blue-600 px-2.5 py-1 rounded-full text-xs font-bold">
                  {selectedOrders.length}
                </span>
              )}
            </button>
          </div>

          <div className="flex lg:flex-row flex-col items-start gap-4">
            <div className="relative flex-1 max-w-2xl">
              <Search
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400"
                size={20}
              />
              <input
                type="text"
                placeholder="Search by quote number, customer, reference, or salesman..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-12 py-3 border-2 border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-slate-50 hover:bg-white transition-colors"
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full p-1 transition-colors"
                >
                  <X size={18} />
                </button>
              )}
            </div>

            {selectedOrders.length > 0 && (
              <div className="flex-1 max-h-[120px] overflow-y-auto">
                <div className="flex items-center gap-2 flex-wrap">
                  {selectedOrders.map((quotNo) => (
                    <div
                      key={quotNo}
                      className="flex items-center gap-1.5 bg-white border border-blue-200 rounded-full px-3 py-1.5 shadow-sm hover:shadow transition-shadow group"
                    >
                      <span className="text-xs font-semibold text-blue-700">
                        {quotNo}
                      </span>
                      <button
                        onClick={() => removeSelection(quotNo)}
                        className="hover:bg-blue-100 rounded-full p-0.5 transition-colors"
                      >
                        <X size={14} className="text-blue-600" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden lg:px-6 px-3 py-4">
        <div className="h-full bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden flex flex-col">
          {loadingModalData ? (
            <div className="flex-1 flex items-center justify-center px-6 py-16">
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                <p className="text-slate-600 text-sm font-medium">
                  Loading {selectedDocType}s...
                </p>
              </div>
            </div>
          ) : orders.length === 0 ? (
            <div className="flex-1 flex items-center justify-center px-6 py-16">
              <div className="text-center">
                <div className="bg-slate-100 rounded-full p-6 inline-block mb-4">
                  <Send size={48} className="text-slate-300" />
                </div>
                <p className="text-slate-700 font-semibold text-lg mb-1">
                  No {selectedDocType} Available
                </p>
                <p className="text-slate-500 text-sm">
                  There are no documents to transfer at this time.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-auto">
              <table className="w-full">
                <thead className="sticky top-0 z-10 bg-gradient-to-r from-slate-100 to-slate-50 border-b-2 border-slate-200 shadow-sm">
                  <tr>
                    <th className="px-6 py-4 text-left bg-slate-100">
                      <input
                        type="checkbox"
                        checked={
                          orders.length > 0 &&
                          orders.every((order) =>
                            selectedOrders.includes(getOrderId(order))
                          )
                        }
                        onChange={handleSelectAll}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                      />
                    </th>
                    {columns?.map((col) => (
                      <th
                        key={col.field}
                        className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider bg-slate-100"
                        style={{ minWidth: col.minWidth }}
                      >
                        {col.headerName}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="bg-white">
                  {orders?.map((order, idx) => (
                    <tr
                      key={getOrderId(order)}
                      ref={rowRefs.current[idx]}
                      className={`transition-all duration-150 ${
                        selectedOrders.includes(getOrderId(order))
                          ? "bg-blue-50 border-l-4 border-blue-500"
                          : "bg-white border-l-4 border-transparent"
                      }`}
                      // onClick={() => handleSelectOrder(getOrderId(order))}
                    >
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          cchecked={selectedOrders.includes(getOrderId(order))}
                          onChange={() => handleSelectOrder(getOrderId(order))}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                        />
                      </td>
                      {columns.map((col) => (
                        <td
                          key={col.field}
                          className={`px-6 py-4 text-sm ${
                            col.type === "number"
                              ? "text-right font-semibold tabular-nums text-slate-900"
                              : "text-slate-700"
                          }`}
                        >
                          {formatCellValue(order[col.field], col.field)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransferOrderModal;
